import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import firebase from "../../services/firebase";
import styled from "styled-components";

// Styled components
const Image = styled.img`
  height: 800px;
`;

const StyledImage = styled.img`
  height: ${(props) => props.$height || "800px"};
  border-radius: 35px;
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-div-md);
  width: auto;
`;

const UploadButton = styled.button`
  margin-top: 20px;
  padding: 10px 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }
`;

const Input = styled.input`
  margin-top: 10px;
`;

function getTodayName() {
  const days = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ];
  const today = new Date().getDay(); // 0 for Sunday, 1 for Monday, etc.
  return days[today];
}

const fetchTodayData = async ({ queryKey }) => {
  const [_, dayName] = queryKey;
  const db = getFirestore(firebase);
  const docRef = doc(db, "lunchMenu", dayName);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data();
  } else {
    throw new Error(`Document for ${dayName} does not exist`);
  }
};

function formatTimestamp(timestamp) {
  if (!timestamp) return "Unknown date";

  let date;

  if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === "object" && "seconds" in timestamp) {
    date = new Date(timestamp.seconds * 1000);
  } else if (typeof timestamp === "string" || typeof timestamp === "number") {
    date = new Date(timestamp);
  } else {
    return "Unknown date";
  }

  if (isNaN(date.getTime())) return "Invalid date";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export default function TodaysPhoto({ height = "800px" }) {
  const todayName = getTodayName();
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);

  const {
    data: todayData,
    isLoading,
    isError,
    error: errorT,
  } = useQuery({
    queryKey: ["todayData", todayName],
    queryFn: fetchTodayData,
  });

  const fetchTodayPicture = async () => {
    const dayName = getTodayName();
    const db = getFirestore(firebase);
    const docRef = doc(db, "lunchMenu", dayName);

    try {
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const newData = snap.data();
        queryClient.setQueryData(["todayData", dayName], newData);
      } else {
        console.warn(`No data for ${dayName}`);
      }
    } catch (err) {
      console.error("Error fetching Firestore data:", err);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const hour = now.getHours();

      if (hour >= 10 && hour < 12) {
        fetchTodayPicture();
      }
    }, 15 * 60 * 1000); // every 15 minutes

    return () => clearInterval(interval);
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      alert("Please select a file first!");
      return;
    }

    const storage = getStorage(firebase);
    const storageRef = ref(storage, `LunchMenu/${todayName}.jpg`);

    try {
      const uploadDate = new Date();
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const db = getFirestore(firebase);
      const docRef = doc(db, "lunchMenu", todayName);

      try {
        await setDoc(
          docRef,
          {
            image: downloadURL,
            uploadDate: uploadDate,
          },
          { merge: true }
        );
        console.log("uploadDate written to Firestore:", uploadDate);
      } catch (firestoreErr) {
        console.error("Failed to set Firestore doc:", firestoreErr);
      }

      queryClient.invalidateQueries(["todayData", todayName]);
      alert("Photo uploaded successfully!");
    } catch (err) {
      console.error("Error uploading photo:", err);
      alert("Error uploading photo!");
    }
  };

  function isToday(timestamp) {
    if (!timestamp) return false;

    let date;

    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === "object" && "seconds" in timestamp) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      return false;
    }

    const now = new Date();
    return (
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate()
    );
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error: {errorT.message}</div>;
  }

  const uploadDate = todayData.uploadDate;
  const isUploadFromToday = isToday(uploadDate);

  return (
    <div>
      {isUploadFromToday ? (
        <>
          <StyledImage
            src={todayData.image}
            alt={`Today's Dish: ${todayData.name}`}
            $height={height}
          />
          {height === "800px" && (
            <>
              <h3>Today&apos;s Dish by {todayData.name}</h3>
              <p>Uploaded on: {formatTimestamp(uploadDate)}</p>
            </>
          )}
        </>
      ) : (
        <p>No photo uploaded today.</p>
      )}
      {height === "800px" && (
        <Input type="file" accept="image/*" onChange={handleFileChange} />
      )}
      {height === "800px" && (
        <UploadButton onClick={handleUpload}>Upload New Photo</UploadButton>
      )}
    </div>
  );
}
